/* CNV-5079 — Homepage Variant B: tabbed product selector.
   One carousel per tab panel; the header arrows drive whichever panel is shown. */
if (!customElements.get('ctr-hp-b-tabs')) {
  customElements.define(
    'ctr-hp-b-tabs',
    class CtrHpBTabs extends HTMLElement {
      connectedCallback() {
        this.tabs = [...this.querySelectorAll('.ctr-hp-b-tabs__tab')];
        this.panels = [...this.querySelectorAll('.ctr-hp-b-tabs__panel')];
        this.prev = this.querySelector('.ctr-hp-b-tabs__arrow--prev');
        this.next = this.querySelector('.ctr-hp-b-tabs__arrow--next');
        this.sliders = new Map();

        this.setScrollbarVar();
        this.panels.forEach((panel) => this.initSlider(panel));

        this.tabs.forEach((tab, i) => {
          tab.addEventListener('click', () => this.select(i));
          tab.addEventListener('keydown', (e) => this.onKeydown(e, i));
        });

        this.prev?.addEventListener('click', () => this.active?.slidePrev());
        this.next?.addEventListener('click', () => this.active?.slideNext());

        this.onResize = this.debounce(() => {
          this.setScrollbarVar();
          this.sliders.forEach((s) => s.update && s.update());
        }, 150);
        window.addEventListener('resize', this.onResize);

        this.syncArrows();
      }

      disconnectedCallback() {
        if (this.onResize) window.removeEventListener('resize', this.onResize);
      }

      debounce(fn, wait) {
        let t;
        return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), wait); };
      }

      setScrollbarVar() {
        const sb = window.innerWidth - document.documentElement.clientWidth;
        this.style.setProperty('--hp-b-scrollbar', `${Math.max(0, sb)}px`);
      }

      get active() {
        const panel = this.panels.find((p) => !p.hasAttribute('hidden'));
        return panel ? this.sliders.get(panel) : null;
      }

      initSlider(panel) {
        const el = panel.querySelector('.ctr-hp-b-tabs__slider');
        const wrapper = panel.querySelector('.ctr-hp-b-tabs__slider-wrapper');
        if (!el || !wrapper) return;

        el.classList.add('swiper');
        wrapper.classList.add('swiper-wrapper');

        const options = {
          slidesPerView: 'auto',
          spaceBetween: 12,
          pagination: { el: panel.querySelector('.ctr-hp-b-tabs__pagination'), clickable: true },
          loop: false,
          threshold: 2,
          watchSlidesProgress: true,
          breakpoints: { 750: { spaceBetween: 24 } },
          on: { slideChange: () => this.syncArrows(), reachEnd: () => this.syncArrows(), reachBeginning: () => this.syncArrows() },
        };

        let instance = null;
        if (window.FoxTheme && window.FoxTheme.Carousel) {
          const c = new window.FoxTheme.Carousel(el, options);
          c.init();
          instance = el.swiper || c;
        } else if (window.Swiper) {
          instance = new window.Swiper(el, options);
        }
        if (instance) {
          this.sliders.set(panel, instance);
          // per-panel arrows (shown on mobile, under the cards)
          panel.querySelector('.ctr-hp-b-tabs__arrows--panel .ctr-hp-b-tabs__arrow--prev')
            ?.addEventListener('click', () => instance.slidePrev());
          panel.querySelector('.ctr-hp-b-tabs__arrows--panel .ctr-hp-b-tabs__arrow--next')
            ?.addEventListener('click', () => instance.slideNext());
        }
      }

      select(index) {
        this.tabs.forEach((tab, i) => {
          const on = i === index;
          tab.classList.toggle('is-active', on);
          tab.setAttribute('aria-selected', on ? 'true' : 'false');
          tab.tabIndex = on ? 0 : -1;
        });
        this.panels.forEach((panel, i) => {
          if (i === index) panel.removeAttribute('hidden');
          else panel.setAttribute('hidden', '');
        });
        const s = this.active;
        if (s && s.update) s.update();
        this.syncArrows();
      }

      onKeydown(e, i) {
        const last = this.tabs.length - 1;
        let target = null;
        if (e.key === 'ArrowRight') target = i === last ? 0 : i + 1;
        else if (e.key === 'ArrowLeft') target = i === 0 ? last : i - 1;
        else if (e.key === 'Home') target = 0;
        else if (e.key === 'End') target = last;
        if (target === null) return;
        e.preventDefault();
        this.select(target);
        this.tabs[target].focus();
      }

      syncArrows() {
        const s = this.active;
        if (!s || !this.prev || !this.next) return;
        this.prev.disabled = !!s.isBeginning;
        this.next.disabled = !!s.isEnd;
      }
    }
  );
}
